<template>
  <lateral-modal
    :title="$t('expenses_edit_expense_header')"
    :containerClass="isDragging && !transactionWithoutReceipt ? 'ModalBox--dim' : ''"
    @close="checkCloseModal"
  >
    <template slot="extraAction">
      <transactions-dropdown
        deleteText="transaction_delete_expense"
        :transactionId.sync="transactionId"
        @confirmDelete="deleteTransaction"
      ></transactions-dropdown>
    </template>
    <template slot="content">
      <modal-drop-zone
        v-if="isDragging && !transactionWithoutReceipt"
        :showDropZone.sync="isDragging"
        @dragleave.prevent="onDragleave"
        @drop.prevent="ondrop"
        :fileCount="receiptCount"
        :showSpinner="fileUploadByDrop"
      />
      <template v-if="initialized && !isSubmitting">
        <form
          class="Form p-2"
          @dragenter.prevent="onDragenter"
        >
          <transaction-review-status-log
            v-if="transactionId && reviewStatus !== 'DRAFT'"
            :transaction="transaction"
          />

          <div class="row">
            <warning-container
              :transaction="transaction"
              @showIgnoreWarning="showIgnoreWarning"
              @showTransactionModal="showTransactionModal"
            />
            <div class="col-12">
              <receipts-container
                ref="receiptsContainer"
                :transactionIsLocked="transactionIsLocked"
                :isProcessing="isProcessing"
                :transactionId="transactionId"
                :receipts="receipts"
                :transactionWithoutReceiptNotes="transactionWithoutReceiptNotes"
                :transactionWithoutReceipt="transactionWithoutReceipt"
                :required="fieldIsRequired('medias')"
                :expenseType="expenseType"
                @showReceiptModal="emitShowReceiptModal"
                @uploadedReceipt="uploadedReceipt"
                @createDraft="createExpense('DRAFT')"
                @sendToReview="submitExpenseHandler"
              ></receipts-container>
            </div>
            <div class="col-12 px-1" v-if="hasManualReimburseOfficeFeature">
              <input-checkbox :id="'make-reimburse-check'" :value="isRefundFlag" @click="handleIsRefundFlag">
                <template v-slot:input-label>
                  <span class="Fuell-Text">{{$t('expenses_mark_as_refund')}}</span>
                </template>
              </input-checkbox>
            </div>
            <div
              class="col-12"
              :style="{ 'order': inputsOrdered.indexOf('paymentMethod') }"
              :tabindex="inputsOrdered.indexOf('paymentMethod')"
            >
              <div class="input--block input--block--margin input--block--padded">
                <label
                  for="transaction-category-input"
                  class="form--label"
                  :class="fieldIsRequired('paymentMethod')"
                >
                  {{ $t("expense_payment_method").toUpperCase() }}
                </label>
                <div
                  class="multiselect-filter"
                  :class="{
                  'multiselect--error': $v.transaction.paymentMethod.$error,
                }"
                >
                  <multiselect
                    v-if="paymentMethods.length > 0"
                    :id="'expense-payment-method-selector'"
                    :options="paymentMethods"
                    :customLabel="(option) => paymentMethodsLabel(option)"
                    v-model="transaction.paymentMethod"
                    :disabled="transactionIsLocked || isProcessing"
                    :placeholder="$t('expense_payment_method')"
                    track-by="id"
                    :allowEmpty="false"
                    :showLabels="false"
                    @open="$v.transaction.paymentMethod.$touch()"
                  >
                    <div
                      class="caret"
                      slot="caret"
                      slot-scope="{ toggle }"
                      @mousedown.prevent.stop="toggle"
                    >
                      <i
                        class="Ctrl Ctrl--16"
                        aria-hidden="true"
                      >
                        <svg
                          focusable="false"
                          aria-hidden="true"
                          e
                          aria-label="Edit"
                        >
                          <use xlink:href="#dropdown-icon"></use>
                        </svg>
                      </i>
                    </div>
                    <template
                      slot="singleLabel"
                      slot-scope="props"
                    >
                      <div
                        class="full-width"
                        v-if="props.option && props.option.officePaymentMethod"
                      >
                        <span>{{ props.option.officePaymentMethod.name }}</span>
                        <span class="multiselect--extra-info-gray">
                          -
                          {{
                            props.option.officePaymentMethod.repayable
                            ? $t("expenses_expense_is_reimbursable")
                            : $t("expenses_expense_is_not_reimbursable")
                          }}</span>
                      </div>
                      <div
                        class="full-width"
                        v-else
                      >
                        <span> {{ $t('expense_payment_method') }}</span>
                      </div>
                    </template>
                  </multiselect>
                </div>
              </div>
            </div>
            <div
              class="col-12"
              :style="{ 'order': inputsOrdered.indexOf('defaultCategory') }"
              :tabindex="inputsOrdered.indexOf('defaultCategory')"
            >
              <div class="input--block input--block--margin input--block--padded">
                <label
                  for="transaction-category-input"
                  class="form--label"
                  :class="fieldIsRequired('defaultCategory')"
                >
                  {{
                    $t("transactions_edit_transaction_category").toUpperCase()
                  }}
                </label>
                <category-selector
                  :id="'expense-category-selector'"
                  :disabled="transactionIsLocked || isProcessing"
                  :selectedCategoryObject.sync="selectedCategoryObject"
                  :userIsOwnerOfTransaction="userIsTransactionOwner"
                  @categorySelected="handleCategorySubcategorySelect($event, 'expense')"
                />
              </div>
            </div>
            <div
              class="col-12"
              :style="{ 'order': inputsOrdered.indexOf('budget') }"
              :tabindex="inputsOrdered.indexOf('budget')"
            >
              <div class="input--block input--block--margin input--block--padded">
                <label
                  for="transaction-budget-selector"
                  class="form--label"
                  :class="fieldIsRequired('budget')"
                >
                  {{ $t("BUDGETS").toUpperCase() }}
                </label>
                <div class="multiselect-filter">
                  <multi-select-input
                    id="expense-budget-selector"
                    :options="generateBudgetList()"
                    :placeholder="$t('BUDGETS')"
                    emitFullValue
                    :value="budget"
                    :disabled="transactionIsLocked || isProcessing"
                    :customLabel="'name'"
                    searchable
                    @inputSelected="chooseBudget($event)"
                  />
                </div>
              </div>
            </div>
            <div
              class="col-12"
              :style="{ 'order': inputsOrdered.indexOf('amount') }"
              :tabindex="inputsOrdered.indexOf('amount')"
            >
              <div class="w-100">
                <div class="input--block input--block--margin input--block--padded">
                  <label
                    for="transaction-budget-selector"
                    class="form--label"
                    :class="fieldIsRequired('amount')"
                  >
                    {{ $t("expense_amount").toUpperCase() }}
                  </label>
                  <div class="amount-block">
                    <multioptions-text
                      :id="'amount-original-currency'"
                      type="money"
                      :class="{ 'amount-selector': showMultiCurrency, 'w-100': !showMultiCurrency }"
                      :options="options.amount"
                      :fieldValue.sync="amountOriginalCurrency"
                      :disabled="transactionIsLocked || isProcessing"
                      :placeholder="getPlaceholder('expense_amount')"
                      :vuelidateElement="$v.amountOriginalCurrency"
                      money
                      @updatedValue="amountUpdated"
                    ></multioptions-text>
                    <div
                      v-if="showMultiCurrency"
                      class="separator-selector"
                    >
                      <div class="line"></div>
                    </div>

                    <div
                      v-if="showMultiCurrency"
                      id="currency-selector"
                    >

                      <multiselect
                        :id="'currency-selector'"
                        :options="currencies"
                        v-model="currency"
                        track-by="id"
                        :label="'code'"
                        :selectLabel="''"
                        :selectedLabel="''"
                        :deselectLabel="''"
                        :disabled="transactionIsLocked || isProcessing"
                        @select="onSelectedCurrency($event)"
                      >
                        <div
                          class="caret"
                          slot="caret"
                          slot-scope="{ toggle }"
                          @mousedown.prevent.stop="toggle"
                        >
                          <i
                            class="Ctrl Ctrl--16"
                            aria-hidden="true"
                          >
                            <svg
                              focusable="false"
                              aria-hidden="true"
                              aria-label="Edit"
                            >
                              <use xlink:href="#dropdown-icon"></use>
                            </svg>
                          </i>
                        </div>
                        <template
                          slot="option"
                          slot-scope="props"
                        >
                          <img
                            :src="props.option.imageUrl"
                            class="mr-2 currency-flag-image"
                            :alt="`icon-currency-flag-${props.option.name}`"
                          />
                          <span>{{ props.option.name }}</span>
                        </template>
                      </multiselect>
                    </div>
                  </div>
                </div>
              </div>
              <div
                v-if="currency.code !== DEFAULT_CURRENCY"
                class="w-100"
              >
                <div class="input--block input--block--margin input--block--padded exchange-rate-block">
                  <div class="transaction-exchange-rate">
                    <label
                      for="transaction-budget-selector"
                      class="form--label u-required"
                    >
                      {{ $t("expense_exchange_rate").toUpperCase() }}
                    </label>
                    <div class="Form-field">
                      <currency-input
                        class="Fuell-Text-Input Fuell-Text-Input--short"
                        :currency="null"
                        :disabled="transactionIsLocked || isProcessing"
                        :placeholder="$t('expense_exchange_rate')"
                        v-model="exchangeRate"
                        :allow-negative="false"
                        @blur="handleCurrencyChange()"
                      >
                      </currency-input>
                    </div>
                  </div>
                  <div class="transaction-default-currency-amount">
                    <label
                      for="transaction-budget-selector"
                      class="form--label"
                    >
                      {{ $t("expense_default_currency_amount").toUpperCase() }}
                    </label>
                    <div class="Form-field">
                      <input
                        type="text"
                        class="Input-text"
                        name="amount-original-currency"
                        disabled
                        :placeholder="'-'"
                        v-model="transaction.amount"
                        id="transaction-amount-currency"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div
              class="col-12"
              :style="{ 'order': inputsOrdered.indexOf('date') }"
              :tabindex="inputsOrdered.indexOf('date')"
            >
              <div class="input--block input--block--margin input--block--padded">
                <label
                  class="form--label"
                  :class="fieldIsRequired('date')"
                >
                  {{ $t("expense_edit_expense_date").toUpperCase() }}</label>
                <multioptions-text
                  type="date"
                  :isPlexus="isPlexus"
                  :options="options.date"
                  :fieldValue.sync="transaction.date"
                  :disabled="transactionIsLocked || isProcessing"
                  :placeholder="getPlaceholder('expense_edit_expense_date')"
                  :vuelidateElement="$v.transaction.date"
                  @inputDate="setExpenseDate($event)"
                ></multioptions-text>
              </div>
            </div>
            <div
              class="col-12"
              :style="{ 'order': inputsOrdered.indexOf('provider') }"
              :tabindex="inputsOrdered.indexOf('provider')"
            >
              <div class="input--block input--block--margin input--block--padded">
                <label
                  for="transaction-bill-type-input"
                  class="form--label"
                  :class="fieldIsRequired('provider')"
                >
                  {{ $t("expense_merchant_name").toUpperCase() }}
                </label>
                <multioptions-text
                  :options="options.provider"
                  :fieldValue.sync="receipt.provider"
                  :disabled="transactionIsLocked || isProcessing"
                  :placeholder="getPlaceholder('expense_merchant_name')"
                  :vuelidateElement="$v.receipt.provider"
                ></multioptions-text>
              </div>
            </div>
            <template v-if="!isFetchingBudgetTagTypes && selectedBudgetMandatoryTagTypes.length > 0">
              <div
                v-for="item in selectedBudgetMandatoryTagTypes"
                :key="item.id"
                :style="{ 'order': inputsOrdered.indexOf('mandatoryTagTypes') }"
                :tabindex="inputsOrdered.indexOf('mandatoryTagTypes')"
                class="input--block input--block--margin input--block--padded mx-2"
              >
                <label
                  for="transaction-tag"
                  class="form--label"
                  :class="{ 'u-required': item.mandatory }"
                >{{
                  item.tagType.name.toUpperCase() }}</label>

                <option-settings-input
                  id="transaction-tag"
                  :disabled="transactionIsLocked || isProcessing"
                  :allowMultiple="item.tagType.multipleOptionsEnabled"
                  :allowTaggable="item.tagType.customOptionEnabled"
                  :alternateOptions="item.tagType.tags"
                  :overrideValue="handleCustomFieldValues(item.tagType)"
                  useOptionSlot
                  :noOptionsLabel="
                      'company_create_transaction_field_options_no_options'
                    "
                  :noResultLabel="'multiselect_no_result_slot_default'"
                  :placeholder="item.tagType.name"
                  :tag-placeholder="
                      $t(
                        handleCreateTagPlaceholder(
                          item.tagType.customOptionEnabled
                        )
                      )
                    "
                  emitTagTypeOnClose
                  :searchable="true"
                  :customField="item.tagType"
                  :class="{ 'multiselect--error': item.invalid }"
                  closeOnSelect
                  internalSearch
                  @select="addTagToTransactionTag(item, $event)"
                  @remove="removeTagFromTransactionTag"
                  @tag="handleCreateOption(item, $event)"
                ></option-settings-input>
              </div>
            </template>
            <div
              class="col-12"
              :style="{ 'order': inputsOrdered.indexOf('notes') }"
              :tabindex="inputsOrdered.indexOf('notes')"
            >
              <div class="input--block input--block--margin input--block--padded">
                <label
                  for="expense-notes"
                  class="form--label"
                  :class="fieldIsRequired('notes')"
                >
                  {{ $t("expense_notes").toUpperCase() }}
                </label>
                <div class="Form-field">
                  <input
                    v-model="transaction.notes"
                    type="text"
                    class="Input-text"
                    name="expense-notes"
                    :disabled="transactionIsLocked || isProcessing"
                    :placeholder="$t('expense_notes')"
                  />
                </div>
              </div>
            </div>
            <div
              class="col-12"
              :style="{ 'order': inputsOrdered.indexOf('receiptNumber') }"
              :tabindex="inputsOrdered.indexOf('receiptNumber')"
            >
              <div class="input--block input--block--margin input--block--padded">
                <label
                  for=""
                  class="form--label"
                  :class="fieldIsRequired('receiptNumber')"
                >
                  {{
                    $t("transaction_edit_receipt_number").toUpperCase()
                  }}</label>
                <multioptions-text
                  :options="options.receiptNumber"
                  :fieldValue.sync="receipt.receiptNumber"
                  :disabled="transactionIsLocked || isProcessing"
                  :placeholder="getPlaceholder('transaction_edit_receipt_number')"
                  :vuelidateElement="$v.receipt.receiptNumber"
                ></multioptions-text>
              </div>
            </div>
            <div
              class="col-12"
              :style="{ 'order': inputsOrdered.indexOf('address') }"
              :tabindex="inputsOrdered.indexOf('address')"
            >
              <div class="input--block input--block--margin input--block--padded">
                <label
                  for=""
                  class="form--label"
                  :class="fieldIsRequired('address')"
                >
                  {{ $t("transaction_edit_address").toUpperCase() }}</label>
                <multioptions-text
                  :options="options.address"
                  :fieldValue.sync="receipt.address"
                  :disabled="transactionIsLocked || isProcessing"
                  :placeholder="getPlaceholder('transaction_edit_address')"
                  :vuelidateElement="$v.receipt.address"
                ></multioptions-text>
              </div>
            </div>
            <div
              class="col-12"
              :style="{ 'order': inputsOrdered.indexOf('zipCode') }"
              :tabindex="inputsOrdered.indexOf('zipCode')"
            >
              <div class="input--block input--block--margin input--block--padded">
                <label
                  for=""
                  class="form--label"
                  :class="fieldIsRequired('zipCode')"
                >
                  {{ $t("transaction_edit_zip_code").toUpperCase() }}</label>
                <multioptions-text
                  :options="options.zipCode"
                  :fieldValue.sync="receipt.zipCode"
                  :disabled="transactionIsLocked || isProcessing"
                  :placeholder="getPlaceholder('transaction_edit_zip_code')"
                  :vuelidateElement="$v.receipt.zipCode"
                ></multioptions-text>
              </div>
            </div>

            <div
              class="col-12"
              :style="{ 'order': inputsOrdered.indexOf('city') }"
              :tabindex="inputsOrdered.indexOf('city')"
            >
              <div class="input--block input--block--margin input--block--padded">
                <label
                  for=""
                  class="form--label"
                  :class="fieldIsRequired('city')"
                >
                  {{ $t("transaction_edit_city").toUpperCase() }}</label>
                <multioptions-text
                  :options="options.city"
                  :fieldValue.sync="receipt.city"
                  :disabled="transactionIsLocked || isProcessing"
                  :placeholder="getPlaceholder('transaction_edit_city')"
                  :vuelidateElement="$v.receipt.city"
                ></multioptions-text>
              </div>
            </div>
            <div
              class="col-12"
              :style="{ 'order': inputsOrdered.indexOf('state') }"
              :tabindex="inputsOrdered.indexOf('state')"
            >
              <div class="input--block input--block--margin input--block--padded">
                <label
                  for=""
                  class="form--label"
                  :class="fieldIsRequired('state')"
                >
                  {{ $t("transaction_edit_state").toUpperCase() }}</label>
                <multioptions-text
                  :options="options.state"
                  :fieldValue.sync="receipt.state"
                  :disabled="transactionIsLocked || isProcessing"
                  :placeholder="getPlaceholder('transaction_edit_state')"
                  :vuelidateElement="$v.receipt.state"
                ></multioptions-text>
              </div>
            </div>
            <div
              class="col-12"
              :style="{ 'order': inputsOrdered.indexOf('country') }"
              :tabindex="inputsOrdered.indexOf('country')"
            >
              <div class="input--block input--block--margin input--block--padded">
                <label
                  for=""
                  class="form--label"
                  :class="fieldIsRequired('country')"
                >
                  {{ $t("transaction_edit_country").toUpperCase() }}</label>
                <multioptions-text
                  :options="options.country"
                  :fieldValue.sync="receipt.country"
                  :disabled="transactionIsLocked || isProcessing"
                  :placeholder="getPlaceholder('transaction_edit_country')"
                  :vuelidateElement="$v.receipt.country"
                ></multioptions-text>
              </div>
            </div>
            <template v-if="!isFetchingBudgetTagTypes && selectedBudgetNoMandatoryTagTypes.length > 0">
              <div
                v-for="item in selectedBudgetNoMandatoryTagTypes"
                :key="item.id"
                :style="{ 'order': inputsOrdered.indexOf('noMandatoryTagTypes') }"
                :tabindex="inputsOrdered.indexOf('noMandatoryTagTypes')"
                class="input--block input--block--margin input--block--padded mx-2"
              >
                <label
                  for="transaction-tag"
                  class="form--label"
                  :class="{ 'u-required': item.mandatory }"
                >{{
                  item.tagType.name.toUpperCase() }}</label>

                <option-settings-input
                  :disabled="transactionIsLocked || isProcessing"
                  :allowMultiple="item.tagType.multipleOptionsEnabled"
                  :allowTaggable="item.tagType.customOptionEnabled"
                  :alternateOptions="item.tagType.tags"
                  :label="'name'"
                  :overrideValue="handleCustomFieldValues(item.tagType)"
                  :noOptionsLabel="
                      'company_create_transaction_field_options_no_options'
                    "
                  useOptionSlot
                  :noResultLabel="'multiselect_no_result_slot_default'"
                  :placeholder="item.tagType.name"
                  :tag-placeholder="
                      $t(
                        handleCreateTagPlaceholder(
                          item.tagType.customOptionEnabled
                        )
                      )
                    "
                  emitTagTypeOnClose
                  :searchable="true"
                  :customField="item.tagType"
                  :class="{ 'multiselect--error': item.invalid }"
                  closeOnSelect
                  @select="addTagToTransactionTag(item, $event)"
                  @remove="removeTagFromTransactionTag"
                  @tag="handleCreateOption(item, $event)"
                ></option-settings-input>
              </div>
            </template>
            <!--- BLOQUE DE IMPUESTOS -->
            <div
              class="input--block input--block--margin input--block--padded mx-2"
              :style="{ 'order': inputsOrdered.indexOf('vatTypeAssigned') }"
              :tabindex="inputsOrdered.indexOf('vatTypeAssigned')"
            >
              <label
                for=""
                class="form--label"
                :class="fieldIsRequired('vatTypeAssigned')"
              >{{
                $t(
                  "transactions_edit_transaction_tax_region_selector"
                ).toUpperCase()
              }}</label>
              <div class="multiselect-filter">
                <multiselect
                  :id="'transaction-tax-region-picker'"
                  :options="vatTypes"
                  :disabled="transactionIsLocked || isProcessing"
                  v-model="selectedVatRegion"
                  :placeholder="$t('transactions_edit_transaction_tax_region_selector')"
                  track-by="id"
                  label="place"
                  :selectLabel="''"
                  :searchable="false"
                  :allowEmpty="false"
                  :showLabels="false"
                  :selectedLabel="''"
                  @input="recalculateVat"
                >
                  <div
                    class="caret"
                    slot="caret"
                    slot-scope="{ toggle }"
                    @mousedown.prevent.stop="toggle"
                  >
                    <i
                      class="Ctrl Ctrl--16"
                      aria-hidden="true"
                    >
                      <svg
                        focusable="false"
                        aria-hidden="true"
                        aria-label="Edit"
                      >
                        <use xlink:href="#dropdown-icon"></use>
                      </svg>
                    </i>
                  </div>
                  <template slot="noOptions">
                    <span class="no-result">
                      {{ $t("multiselect_no_options_slot_default") }}
                    </span>
                  </template>
                  <template
                    slot="singleLabel"
                    slot-scope="props"
                  >
                    <div class="d-flex">
                      <div class="flag-container">
                        <img
                          :src="getFlagIcon(props.option)"
                          class="mr-2 currency-flag-image"
                          :alt="`icon-flag-${props.option.place}`"
                        />
                        <span class="my-auto ml-2">{{ props.option.place }}</span>
                      </div>
                    </div>
                  </template>
                  <template
                    slot="option"
                    slot-scope="props"
                  >
                    <div class="d-flex">
                      <div class="flag-container my-auto">
                        <img
                          :src="getFlagIcon(props.option)"
                          class="mr-2 currency-flag-image"
                          :alt="`icon-flag-${props.option.place}`"
                        />
                        <span class="my-auto ml-2">{{ props.option.place }}</span>
                      </div>
                    </div>
                  </template>
                </multiselect>
              </div>
            </div>
            <div
              class="col-12 mt-1"
              :style="{ 'order': inputsOrdered.indexOf('taxes') }"
              :tabindex="inputsOrdered.indexOf('taxes')"
            >
              <div class="input-block input--block--margin input--block--paded">
                <!-- Bloque 21% --->
                <div
                  class="d-flex justify-content-between mb-2"
                  v-for="(vatTypeValue, index) in orderedVatTypes()"
                  :key="vatTypeValue[0]"
                >

                  <!-- TAX BASE -->
                  <div class="d-flex flex-column">
                    <label
                      class="form--label pl-2"
                      v-if="index === 0"
                      :class="{ 'u-required': fieldIsRequired('taxes') }"
                    >
                      {{ $t("transactions_edit_tax_base").toUpperCase() }}
                    </label>
                    <div class="d-flex align-items-center">
                      <div>
                        <multioptions-text
                          :options="getOptions(`taxBase${index+1}`)"
                          money
                          :type="'money'"
                          :containerClass="'Form-field Form-field--short-tax'"
                          :fieldValue.sync="taxes[vatTypeValue[0]].taxBase"
                          :disabled="transactionIsLocked || isProcessing"
                          :class="{
                            'tax-zero-color': taxes[vatTypeValue[0]].taxBase === 0,
                          }"
                          :manualErrorFlag="taxes[vatTypeValue[0]].error || taxesRequiredValidationError"
                          @focus="clearZeroTax(vatTypeValue[0])"
                          @blur="handleVatCalculation(
                            taxes[vatTypeValue[0]].taxBase,
                            vatTypeValue[1],
                            vatTypeValue[0]
                          ); checkVat(); validateTaxesRequired()
                            "
                        >
                        </multioptions-text>
                      </div>
                      <span class="currency-symbol">{{ getCurrencySymbol() }}</span>
                    </div>
                  </div>

                  <!-- ARROW BLOCK --->
                  <div class="d-flex align-items-center">
                    <span
                      class="
                            Fuell-aeat-arrow-right Fuell-aeat-arrow-right--pointer
                          "
                      :class="{'Fuell-aeat-arrow-right--first' : index === 0}"
                    >
                      <span class="Fuell-aeat-arrow-right--text">{{ vatTypeValue[1] }}%</span>
                    </span>
                  </div>

                  <!-- VAT BLOCK-->
                  <div class="d-flex flex-column">
                    <label
                      class="form--label pl-2"
                      v-if="index === 0"
                    >
                      {{ $t("transactions_edit_quota").toUpperCase() }}</label>
                    <div class="
                          Form-field Form-field--white Form-field--short
                          position-relative
                        ">
                      <currency-input
                        class="
                            Fuell-Text-Input
                            Fuell-Text-Input--white
                            Fuell-Text-Input--short
                            Fuell-Text-Input--border-bottom
                          "
                        disabled
                        :currency="null"
                        :value="taxes[vatTypeValue[0]].vat"
                        :class="{ 'tax-zero-color': taxes[vatTypeValue[0]].vat === 0 }"
                        :allow-negative="false"
                      ></currency-input>
                      <span class="currency-symbol position-absolute">{{ getCurrencySymbol() }}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div
                v-if="taxesError"
                class="taxes-error"
              >
                <img
                  alt="transaction-warning-icon"
                  :src="require(`@/assets/img/icons/icon-warning-red.svg`)"
                />
                <span>{{ $t('transaction_taxes_error') }}</span>
              </div>
            </div>
            <!----->
            <div
              class="col-12"
              :style="{ 'order': inputsOrdered.indexOf('providerData') }"
              :tabindex="inputsOrdered.indexOf('providerData')"
            >
              <div class="input--block input--block--margin input--block--padded">
                <label
                  for=""
                  class="form--label"
                  :class="fieldIsRequired('providerData')"
                >{{
                  $t("transactions_edit_cif_field").toUpperCase() }}</label>
                <multioptions-text
                  :options="options.providerData"
                  :fieldValue.sync="receipt.providerData"
                  :disabled="transactionIsLocked || isProcessing"
                  :placeholder="getPlaceholder('transactions_edit_cif_field')"
                  :vuelidateElement="$v.receipt.providerData"
                ></multioptions-text>
              </div>
            </div>
          </div>
        </form>
      </template>
      <template v-else>
        <div class="w-100 h-100 d-flex justify-content-center align-items-center">
          <spinner></spinner>
        </div>
      </template>
    </template>
    <template
      slot="footer"
      v-if="initialized"
    >
      <div
        class="d-flex"
        :class="{'flex-column' : (showSaveAsDraftButton && !showSendToReviewButton) || (!showSaveAsDraftButton && showSendToReviewButton) ,
        'justify-content-between' : (showSaveAsDraftButton && showSendToReviewButton)
      }"
      >
        <button
          class="Fuell-Button orange mb-2 flex-grow-1"
          v-if="showSaveAsDraftButton"
          :disabled="transactionIsLocked || isSubmitting || !isDirty || isProcessing"
          :class="{
          disabled: transactionIsLocked || isSubmitting || !isDirty || isProcessing,
        }"
          @click="draftHandler"
        >
          <span class="Fuell-Button--label w-100 text-center">
            {{ $t("expenses_save_as_draft") }}
          </span>
        </button>
        <button
          class="Fuell-Button blue mb-2 flex-grow-1"
          v-if="showSendToReviewButton"
          :disabled="transactionIsLocked || isSubmitting || !canBeSubmitted || isProcessing"
          :class="{
          disabled: transactionIsLocked || isSubmitting || !canBeSubmitted || isProcessing,
          'ml-1' : showSaveAsDraftButton
        }"
          @click="submitExpenseHandler"
        >
          <span class="Fuell-Button--label w-100 text-center">
            {{ $t("expenses_send_to_review") }}
          </span>
        </button>
      </div>
      <button
        v-if="showSaveButton"
        class="Fuell-Button blue mb-2"
        :disabled="transactionIsLocked || isSubmitting || !isDirty || !canBeSaved || isProcessing"
        :class="{
          disabled: transactionIsLocked || isSubmitting || !isDirty || !canBeSaved || isProcessing
        }"
        @click="submitExpenseHandler"
      >
        <span class="Fuell-Button--label w-100 text-center">
          {{ $t("transactions_edit_transaction_save") }}
        </span>
      </button>
      <!-- FUNNEL ACTIONS -->
      <template v-if="!transactionIsLocked &&
        !isProcessing &&
        reviewStatus !== 'DRAFT' &&
        transactionId &&
        !isInExpensesSection && !isDirty
      ">
        <funnel-actions
          @reviewStatusChanged="emitReviewStatusChanged"
          :transactionType="'EXPENSE'"
          :reviewStatus.sync="reviewStatus"
          :budgetOwner="isBudgetOwner"
          :expenseOwner="transactionOwner"
          :transactionId.sync="transactionId"
        />
      </template>
    </template>
    <template slot="modals">
      <transaction-not-receipt-modal
        v-if="showNotReceiptModalFlag"
        :open.sync="showNotReceiptModalFlag"
        @emitNotReceiptReason="handleNotReceipt"
      ></transaction-not-receipt-modal>
      <confirm-modal
        v-if="showSendToReviewWithoutReceipt"
        :open.sync="showSendToReviewWithoutReceipt"
        title="expenses_send_to_review"
        message="expenses_send_to_review_description"
        modalClass="ModalBox--400"
        acceptText="expenses_upload_receipt"
        dismissText="transaction_dont_have_receipt"
        acceptBtnClass="blue"
        @accept="showReceiptsContainer"
        @dismiss="showNotReceiptModal"
      ></confirm-modal>
      <confirm-modal
        v-if="showCloseWarning"
        :open.sync="showCloseWarning"
        title="transaction_close_without_save"
        message="transaction_close_without_save_description"
        modalClass="ModalBox--400"
        acceptText="transaction_close_delete"
        dismissText="transaction_close_delete_draft"
        acceptBtnClass="Button--danger"
        @accept="closeModal"
        @dismiss="draftHandler"
      ></confirm-modal>
      <confirm-modal
        v-if="showIgnoreWarningModal"
        :open.sync="showIgnoreWarningModal"
        :modalClass="'ModalBox--400'"
        :title="'transaction_warning_ignore_modal'"
        :message="'transaction_warning_ignore_message'"
        :acceptText="'transaction_warning_ignore'"
        :dismissText="'ACTION_CANCEL'"
        acceptBtnClass="blue"
        dismissBtnClass="red"
        @accept="ignoreWarning"
        @dismiss="dismissWarning"
      ></confirm-modal>
      <confirm-modal
        v-if="showUploadInProgress"
        :open.sync="showUploadInProgress"
        :modalClass="'ModalBox--400'"
        :title="''"
        :message="''"
        :acceptText="'transaction_upload_in_progress_understood'"
        acceptBtnClass="blue"
        closeButton
        hideDismissButton
        noPadding
        noMargin
        @accept="uploadingAccept"
      >
        <template v-slot:content>
          <div class="transaction-upload-in-progress">
            <img
              :src="require('@/assets/img/upload-in-progress.svg')"
              alt=""
            />
            <p class="transaction-upload-in-progress--title">{{ $t('transaction_upload_in_progress_title') }}</p>
            <p class="transaction-upload-in-progress--description">{{ $t('transaction_upload_in_progress_description') }}
            </p>
          </div>
        </template>
      </confirm-modal>
      <choose-media-type-modal
        v-if="showMediaTypeModalFlag"
        :open.sync="showMediaTypeModalFlag"
        @typeSelected="typeIsSelected"
      ></choose-media-type-modal>
    </template>
  </lateral-modal>
</template>
<script>
import { DEFAULT_CURRENCY } from '@/utils/constants'
import { REVIEW_STATUS } from '@/constants/reviewStatus'
import { getCurrencySymbol } from '@/filters'
import _modalHandlerMixin from '@/components/Commons/_mixins/_modalHandlerMixin'
import _transactionAndExpenseCommon from '../../_mixins/_transactionAndExpenseCommon'
import _transactionReceiptDragAndDropHandler from '../../_mixins/_transactionReceiptDragAndDropHandler'
import _transactionTaxesHandler from '../../_mixins/_transactionTaxesHandler'
import _transactionReceiptEntityHandler from '../../_mixins/_transactionReceiptEntityHandler'
import _expenseBudgetSelectionHandlerMixin from '../../_mixins/_expenseBudgetSelectionHandlerMixin'
import _revissionFunnelActionsMixin from '../../_mixins/funnel/_revissionFunnelActionsMixin'
import _transactionsStatusHandlerMixin from '../../_mixins/_transactionStatusHandlerMixin'
import _transactionsCustomFieldsHandler from '../../_mixins/_transactionsCustomFieldsHandler'
import _transactionsValidationHandlerMixin from '../../_mixins/_transactionsValidationHandlerMixin'
import _expenseIntervalHandlerMixin from '../../_mixins/_expenseIntervalHandlerMixin'
import _transactionsTicketHandlerMixin from '../../_mixins/_transactionsTicketHandlerMixin'
import _multiCurrencyHandlerMixin from '../../_mixins/multicurrency/_multiCurrencyHandlerMixin'

import ModalDropZone from '@/components/Commons/ModalDropZone'
import FunnelActions from '../funnel/FunnelActions.vue'
import { CurrencyInput } from 'vue-currency-input'
import WarningContainer from '../_shared/WarningContainer.vue'
import MultiSelectInput from '@/components/Commons/Forms/MultiSelectInput'
import CategorySelector from '../_shared/CategorySelector.vue'
import Multiselect from 'vue-multiselect'
import Spinner from '@/components/Commons/Spinner'
import OptionSettingsInput from '@/modules/company/_components/_shared/OptionSettingsInput'
import LateralModal from '@/components/Commons/Modals/LateralModal'
import InputCheckbox from '@/components/Commons/Forms/InputCheckbox'
import { INCLUDE_IN_TRANSACTION_TYPES } from '../../_constants/constants'
import ReceiptsContainer from '@/components/Commons/ReceiptsContainer.vue'
import TransactionReviewStatusLog from '../funnel/statusLog/TransactionReviewStatusLog.vue'
import MultioptionsText from '@/components/Commons/MultioptionsText'
const TransactionsDropdown = () => import('../_shared/TransactionsDropdown')
const ChooseMediaTypeModal = () => import('@/modules/transactions/_components/media/ChooseMediaTypeModal.vue')
const TransactionNotReceiptModal = () => import('../_shared/TransactionNotReceiptModal')
const ConfirmModal = () => import('@/components/Commons/ConfirmModal')

export default {
  name: 'CreateExpenseModal',
  mixins: [
    _modalHandlerMixin,
    _transactionAndExpenseCommon,
    _transactionReceiptDragAndDropHandler,
    _transactionTaxesHandler,
    _transactionReceiptEntityHandler,
    _expenseBudgetSelectionHandlerMixin,
    _revissionFunnelActionsMixin,
    _transactionsStatusHandlerMixin,
    _transactionsCustomFieldsHandler,
    _transactionsValidationHandlerMixin,
    _expenseIntervalHandlerMixin,
    _transactionsTicketHandlerMixin,
    _multiCurrencyHandlerMixin
  ],
  components: {
    WarningContainer,
    CurrencyInput,
    ReceiptsContainer,
    Spinner,
    ModalDropZone,
    Multiselect,
    CategorySelector,
    MultiSelectInput,
    FunnelActions,
    OptionSettingsInput,
    TransactionReviewStatusLog,
    MultioptionsText,
    TransactionsDropdown,
    ConfirmModal,
    LateralModal,
    ChooseMediaTypeModal,
    TransactionNotReceiptModal,
    InputCheckbox
  },
  data () {
    return {
      showSendToReviewWithoutReceipt: false,
      showNotReceiptModalFlag: false,
      selectedCategoryObject: {},
      budget: null,
      receipt: {},
      // originalCurrency: null,
      // amountOriginalCurrency: null,
      budgetId: null,
      receiptLocalId: 1,
      initialized: false,
      isSubmitting: false,
      sendToDevoluivaFlag: false,
      expenseType: 'EXPENSE',
      showIgnoreWarningModal: false,
      transactionTags: [],
      withoutTicket: {},
      isRefundFlag: false
    }
  },
  computed: {
    currencies () {
      return this.$store.getters['ConfigStore/currencies']
    },
    hasLocalReceipts () {
      return this.expensesReceipts.length > 0
    },
    canBeSubmitted () {
      if (this.fieldIsRequired('taxes')) {
        return !this.$v.$invalid && this.budgetTagTypes.filter(item => item.mandatory && !item.filled).length === 0 && !this.taxesInvalid
      } else {
        return !this.$v.$invalid && this.budgetTagTypes.filter(item => item.mandatory && !item.filled).length === 0
      }
    },
    canBeSaved () {
      return !this.$v.$invalid
    },
    isSuperBook () {
      return this.$store.getters['LoginStore/isSuperBookKeeper']
    },
    isDirty () {
      /*
      - ** Impuestos, al hacer focus ya hay cambio, revisar el focus
      - ** Mejorar comparación del modelo de fechas, al mover la fecha y volver a ponerla detecta que ha cambiado, probablemente por el formato
      */
      const original = { ...this.originalTransactionModel }
      const current = { ...this.generateTransactionModel() }
      return JSON.stringify(original) !== JSON.stringify(current)
    },
    expenseIsReimbursement () {
      return this.transaction.paymentMethod?.officePaymentMethod?.repayable
    },
    officeActivatedFeatures () {
      return this.$store.getters['CompanyStore/currentCompany'].activatedFeatures
    },
    officeFeatureList () {
      return this.officeActivatedFeatures.map(item => item.feature.code)
    },
    showMultiCurrency () {
      return this.officeFeatureList.includes('MULTI_CURRENCIES_FEATURE')
    },
    hasManualReimburseOfficeFeature () {
      return this.officeFeatureList.includes('MANUAL_EXPENSES_REIMBURSE')
    },
    userIsTransactionOwner () {
      if (!this.transactionId) {
        return true
      } else {
        return this.transactionOwner ? this.transactionOwner.id === this.currentUserId : false
      }
    }
  },
  methods: {
    setBudgetTagTypes () {
      this.selectedBudgetMandatoryTagTypes = this.transaction.budget
        ? this.transaction.budget?.budgetTagTypes?.filter((el) => {
          if (el.mandatory) {
            return el.tagType.includeInTransactionTypes.includes(
              INCLUDE_IN_TRANSACTION_TYPES.EXPENSE
            )
          }
        })
        : []

      this.selectedBudgetNoMandatoryTagTypes = this.transaction.budget
        ? this.transaction.budget?.budgetTagTypes?.filter((el) => {
          if (!el.mandatory) {
            return el.tagType.includeInTransactionTypes.includes(
              INCLUDE_IN_TRANSACTION_TYPES.EXPENSE
            )
          }
        })
        : []
    },
    getCurrencySymbol () {
      let currencySymbol = this.transaction?.currencyAmount?.originalCurrencyCode || DEFAULT_CURRENCY
      return getCurrencySymbol(currencySymbol)
    },
    getPlaceholder (text) {
      return this.isProcessingReceipts ? this.$t('transactions_receipt_processing') + ' ' + this.$t(text).toLowerCase() : this.$t(text)
    },
    // handleExchangeRate (event) {
    //   console.log('Handle exchange rate receives', event)
    //   if (this.transaction.currencyAmount?.exchangeRate) {
    //     this.handleAmountCurrency()
    //   }
    // },
     handleIsRefundFlag () {
      if (!this.hasManualReimburseOfficeFeature) return
      this.isRefundFlag = !this.isRefundFlag
    },

    // handleAmountCurrency () {
    //   if (this.transaction.currencyAmount?.exchangeRate) this.transaction.amount = Math.round(((this.amountOriginalCurrency / this.transaction.currencyAmount.exchangeRate) + Number.EPSILON) * 100) / 100
    // },
    showReceiptsContainer () {
      this.$refs.receiptsContainer.addReceipt(true)
    },
    showNotReceiptModal () {
      this.showNotReceiptModalFlag = !this.showNotReceiptModalFlag
    },
    handleNotReceipt (reason) {
      this.withoutTicket.withoutTicket = true
      this.withoutTicket.withoutTicketNotes = reason
      this.submitExpenseHandler()
    },
    amountUpdated () {
      // Esta logica se tiene que mover
      if (this.exchangeRate && this.currency?.code !== DEFAULT_CURRENCY) {
        this.transaction.amount = this.calculateConversionRate(this.amountOriginalCurrency, this.exchangeRate)
        // Math.round(((this.amountOriginalCurrency / this.transaction.currencyAmount.exchangeRate) + Number.EPSILON) * 100) / 100
      } else {
        this.transaction.amount = this.amountOriginalCurrency
      }
      this.$v.transaction.amount.$touch()
      this.handleTaxInitialization(this.selectedCategoryObject)
      this.checkVat()
      this.handleCurrencyChange()
    },
    handleCategorySubcategorySelect (subcategory) {
      this.selectedCategoryObject = subcategory
      this.selectedCategoryId = subcategory.categoryId
      this.selectedSubCategory = subcategory.subcategoryId
      this.handleTaxInitialization(subcategory)
    },
    setField (fieldName, value) {
      this[fieldName] = value
    },
    setExpenseDate (event) {
      if (this.isPlexus) event = this.$moment.utc(event).set({ hour: 0, minute: 0, second: 0, millisecond: 0 }).format('YYYY-MM-DDTHH:mm:SS+00:00')
      this.transaction.date = event
    },
    async draftHandler () {
      if (this.isSubmitting) return
      if (this.transaction?.id) {
        await this.editExpense(REVIEW_STATUS.DRAFT)
      } else {
        await this.createExpense(REVIEW_STATUS.DRAFT)
      }
    },

    async submitExpenseHandler () {
      if (this.fieldIsRequired('medias') && !this.transaction.withoutTicket && (!this.transaction.receipt?.receiptMedia || this.transaction.receipt?.receiptMedia?.length === 0) && !this.withoutTicket.withoutTicket) {
        this.showSendToReviewWithoutReceipt = true
      } else {
        if (this.transaction?.id) {
          if (this.reviewStatus === REVIEW_STATUS.DRAFT) {
            await this.editExpense(REVIEW_STATUS.AWAITING_REVIEW)
            return
          } else {
            await this.editExpense()
            return
          }
        }
        await this.createExpense(REVIEW_STATUS.AWAITING_REVIEW)
      }
    },
    generateTransactionModel () {
      const transactionModel = {
        id: this.transaction.id
        // ...this.transaction.currencyAmount
      }

      transactionModel.notes = this.transaction.notes

      transactionModel.date = this.transaction.date
      transactionModel.paymentMethod = this.transaction.paymentMethod
      if (this.showMultiCurrency) {
        transactionModel.originalCurrency = this.currency
        transactionModel.amountOriginalCurrency = -Math.abs(this.amountOriginalCurrency)
        transactionModel.exchangeRate = this.exchangeRate
      }
      if (this.currency?.code !== DEFAULT_CURRENCY && this.exchangeRate && this.showMultiCurrency) {
        transactionModel.amount = this.calculateConversionRate(this.amountOriginalCurrency, this.exchangeRate)
      } else {
        transactionModel.amount = -Math.abs(this.amountOriginalCurrency)
      }

      // if (this.originalCurrency.code !== DEFAULT_CURRENCY && this.transaction.currencyAmount?.exchangeRate) {
      //   transactionModel.amount = -Math.round(((Math.abs(this.transaction.currencyAmount?.amountOriginalCurrency) / this.transaction.currencyAmount.exchangeRate) + Number.EPSILON) * 100) / 100
      //   transactionModel.exchangeRate = this.transaction.currencyAmount?.exchangeRate
      //   transactionModel.originalCurrency = { id: this.originalCurrency.id }
      // }
      if (this.isRefundFlag && this.hasManualReimburseOfficeFeature) {
        transactionModel.amount = Math.abs(transactionModel.amount)
        transactionModel.amountOriginalCurrency = Math.abs(transactionModel.amount)
      }

      if (this.budget) {
        transactionModel.budget = { id: this.budget.id }
      }
      if (this.receipt) {
        transactionModel.receipt = { ...this.receipt }
      }
      if (this.files) {
        transactionModel.files = this.files
      }

      if (this.transaction.customCategory) {
        transactionModel.customCategory = {
          id: this.transaction.customCategory.id
        }
      }

      if (
        this.selectedCategoryObject &&
        this.selectedCategoryObject.subcategoryId
      ) {
        transactionModel.customCategory = {
          id: this.selectedCategoryObject.subcategoryId
        }
      }

      if (this.withoutTicket.withoutTicket) {
        transactionModel.withoutTicket = this.withoutTicket.withoutTicket
        transactionModel.withoutTicketNotes = this.withoutTicket.withoutTicketNotes
      }

      if (this.transactionTags.length > 0) {
        const formatedTransactionTags = this.transactionTags.map(el => {
          if (typeof el.tag.id === 'string') {
            const formated = {
              tag: {
                name: el.tag.name,
                type: { ...el.tag.type }
              }
            }
            return formated
          } else {
            return { tag: el.tag }
          }
        })

        transactionModel.transactionTags = formatedTransactionTags
      } else {
        transactionModel.transactionTags = []
      }

      transactionModel.receipt.vat1 = this.taxes.vatType1.vat
      transactionModel.receipt.taxBase1 = this.taxes.vatType1.taxBase
      transactionModel.receipt.vatType1 =
        this.selectedVatRegion.vatType1 === null
          ? 0
          : this.selectedVatRegion.vatType1

      transactionModel.receipt.vat2 = this.taxes.vatType2.vat
      transactionModel.receipt.taxBase2 = this.taxes.vatType2.taxBase
      transactionModel.receipt.vatType2 =
        this.selectedVatRegion.vatType2 === null
          ? 0
          : this.selectedVatRegion.vatType2

      transactionModel.receipt.vat3 = this.taxes.vatType3.vat
      transactionModel.receipt.taxBase3 = this.taxes.vatType3.taxBase
      transactionModel.receipt.vatType3 =
        this.selectedVatRegion.vatType3 === null
          ? 0
          : this.selectedVatRegion.vatType3

      transactionModel.receipt.vat4 = this.taxes.vatType4.vat
      transactionModel.receipt.taxBase4 = this.taxes.vatType4.taxBase
      transactionModel.receipt.vatType4 =
        this.selectedVatRegion.vatType4 === null
          ? 0
          : this.selectedVatRegion.vatType4
      transactionModel.receipt.vat5 = this.taxes.vatType5.vat
      transactionModel.receipt.taxBase5 = this.taxes.vatType5.taxBase
      transactionModel.receipt.vatType5 =
        this.selectedVatRegion.vatType5 === null
          ? 0
          : this.selectedVatRegion.vatType5

      transactionModel.receipt.vat6 = this.taxes.vatType6.vat
      transactionModel.receipt.taxBase6 = this.taxes.vatType6.taxBase
      transactionModel.receipt.vatType6 =
        this.selectedVatRegion.vatType6 === null
          ? 0
          : this.selectedVatRegion.vatType6

      if (this.selectedVatRegion && this.selectedVatRegion.id) {
        transactionModel.receipt.vatTypeAssigned = { id: this.selectedVatRegion.id }
      }

      return transactionModel
    },

    async createExpense (reviewStatus) {
      if (this.isSubmitting) return

      this.isSubmitting = true
      const expenseModel = {
        type: { id: 1 },
        notes: this.transaction.notes || '',
        date: this.transaction.date || null,
        budget: this.budget?.id ? { id: this.budget.id } : undefined,
        paymentMethod: this.transaction.paymentMethod?.id ? { id: this.transaction.paymentMethod.id } : null,
        amount: -Math.abs(this.amountOriginalCurrency),
        transaction: {
          ...this.generateTransactionModel()
        }
      }

      expenseModel.reviewStatusCode = reviewStatus

      if (this.isRefundFlag && this.hasManualReimburseOfficeFeature) {
        expenseModel.amount = Math.abs(expenseModel.amount)
      }

      try {
        await this.$store.dispatch('TransactionsStore/createTransaction', expenseModel)
        const files = []
        const types = []
        this.expensesReceipts.forEach((el) => {
          files.push(el.file)
          types.push(el.media.type)
        })

        await this.$store.dispatch(
          'TransactionsStore/submitTransactionReceipt',
          { id: this.newExpense, files: files, types: types }
        ).then((response) => {
          if (response !== 'error') {
            this.setIntervalTransaction(this.transaction.id)
          }
        })
        this.$emit('update:transactionId', this.newExpense)
        this.$emit('expenseCreated', { force: true })
        this.$store.dispatch('TransactionsStore/resetExpenseReceipts')

        if (reviewStatus !== REVIEW_STATUS.DRAFT) {
          this.closeModal()
        }
      } catch (error) {
        console.error(error)
      } finally {
        this.isSubmitting = false
        if (this.$route.name !== 'my_history_reimbursements') {
          this.$router.push({ name: 'my_history_reimbursements' })
        }
      }
    },
    async editExpense (reviewStatus) {
      if (this.isSubmitting) return
      try {
        this.isSubmitting = true
        let transactionModel = {
          ...this.generateTransactionModel(),
          expense: {
            id: this.expenseId,
            type: { id: this.transaction.expense.type.id },
            paymentMethod: this.transaction.paymentMethod,
            date: this.transaction.date
            // amount: -Math.abs(this.amountOriginalCurrency),
          }
        }
               if (this.currency?.code !== DEFAULT_CURRENCY && this.exchangeRate && this.showMultiCurrency) {
                 transactionModel.expense['amountOriginalCurrency'] = -Math.abs(this.amountOriginalCurrency)
        transactionModel.expense['amount'] = this.calculateConversionRate(this.amountOriginalCurrency, this.exchangeRate)
      } else {
        transactionModel.expense['amount'] = -Math.abs(this.amountOriginalCurrency)
      }

      if (this.isRefundFlag && this.hasManualReimburseOfficeFeature) {
        transactionModel.amount = Math.abs(transactionModel.amount)
        transactionModel.amountOriginalCurrency = Math.abs(transactionModel.amount)
      }

        // if (this.originalCurrency.code !== DEFAULT_CURRENCY && this.transaction.currencyAmount?.exchangeRate) {
        //   transactionModel.expense.amount = -Math.round(((Math.abs(this.transaction.currencyAmount?.amountOriginalCurrency) / this.transaction.currencyAmount.exchangeRate) + Number.EPSILON) * 100) / 100
        //   transactionModel.expense.exchangeRate = this.transaction.currencyAmount?.exchangeRate
        //   transactionModel.expense.originalCurrency = { id: this.originalCurrency.id }
        // }
        if (reviewStatus) {
          const { id } = this.reviewStatusTypes.find(
            (el) => el.code === reviewStatus
          )
          if (id) {
            transactionModel.reviewStatus = { id: id }
          }
        }

        if (this.isRefundFlag && this.hasManualReimburseOfficeFeature) {
         transactionModel.expense.amount = Math.abs(transactionModel.expense.amount)
         transactionModel.expense.amountOriginalCurrency = Math.abs(transactionModel.expense.amountOriginalCurrency)
        }
        await this.$store.dispatch('TransactionsStore/editTransaction', {
          transactionInformation: transactionModel,
          originalTransaction: this.transaction
        })
        this.closeModal()
      } catch (error) {
        console.error(error)
        this.$store.dispatch('SnackBars/addErrorHandler', {
          error: error,
          errorMessage: 'expense_edit_expense_error'
        })
      } finally {
        this.isSubmitting = false
      }
    },

    _initializeExpense () {
      this.budget = this.transaction.budget ? this.transaction.budget : ''
      this.receipt = this.transaction.receipt ? this.transaction.receipt : ''
      this.transactionTags = this.transaction.transactionTags
        ? this.transaction.transactionTags
        : []
      if (this.transaction.amount > 0 && this.hasManualReimburseOfficeFeature) {
        this.isRefundFlag = true
      } else {
        this.isRefundFlag = false
      }
      this.selectedBudgetTagTypes = this.transaction.budget ? this.transaction.budget.budgetTagTypes.filter(el => el.tagType.includeInTransactionTypes.includes(INCLUDE_IN_TRANSACTION_TYPES.TRANSACTION)) : []
      if (!this.isProcessingReceipts && this.transaction.amount !== 0) {
         this.transaction.amount = Math.abs(this.transaction.amount)
         this.exchangeRate = this.transaction.currencyAmount?.exchangeRate

      //  if (!this.transaction.currencyAmount.originalCurrencyCode || this.transaction.currencyAmount.originalCurrencyCode === '') {
      //      this.transaction.currencyAmount.originalCurrencyCode = DEFAULT_CURRENCY
      //   }
       this.currency = this.currencies.find(item => item.code === this.transaction.currencyAmount.originalCurrencyCode) ?? this.currencies.find(item => item.code === DEFAULT_CURRENCY)
      if (this.transaction.currencyAmount.exchangeRate && this.transaction?.currencyAmount?.exchangeRate !== DEFAULT_CURRENCY) {
         this.amountOriginalCurrency = this.transaction.currencyAmount.amountOriginalCurrency
       } else {
         this.amountOriginalCurrency = this.transaction.amount
        }
      }

      this.transactionReviewedFlag = this.transaction.reviewedAt !== null
      this.sendToDevoluivaFlag = this.transaction.receipt ? this.transaction.receipt.doNotSendDevoluiva : false
      this.initializeTaxes()
      this.originalTransactionModel = this.generateTransactionModel()
    },
    async chooseBudget (budget) {
      this.setBudget(budget, INCLUDE_IN_TRANSACTION_TYPES.EXPENSE)
    }
  },
  async created () {
    this.initialized = false
    this.currency = this.currencies.find(item => item.code === DEFAULT_CURRENCY)
    // this.originalOriginalCurrency = { ...this.originalCurrency }
    this.$store.dispatch('TransactionsStore/clearCurrentTransaction')
    const promises = []
    if (this.vatTypes.length === 0) {
      promises.push(this.$store.dispatch('TransactionsStore/fetchVatTypes'))
    }

    if (this.transactionId) {
      await this.fetchTransaction()
      if (this.isSuperBook) {
        const categoriesFilters = {
          'transaction.id': this.transactionId
        }
        promises.push(
          this.$store.dispatch(
            'CategoriesStore/setCategoriesFilters',
            categoriesFilters
          )
        )
      }
    }
    promises.push(this.fetchCurrentTransactionUserBudgets())
    promises.push(this.fetchPaymentMethods())

    Promise.allSettled(promises).then(() => {
      this.initializeTaxes()
      if (this.transactionId) {
        this._initializeExpense()
        this.getBudgetTagTypes(INCLUDE_IN_TRANSACTION_TYPES.EXPENSE)
      } else {
        if (this.budgets.length === 1) {
          this.budget = this.budgets[0]?.budget
          this.getBudgetTagTypes(this.transactionType)
        }
      }
      this.checkPaymentMethodInList()
      if (this.paymentMethods.length === 1) {
        this.transaction.paymentMethod = { ...this.paymentMethods[0] }
      }
      this.orderInputs()
      this.calculateOptions()
      this.setBudgetTagTypes()
      this.initialized = true
    })
    if (this.transaction?.id && this.transaction?.receipt?.receiptMedia.length > 0) {
      this.setIntervalTransaction(this.transaction.id)
    }
  },
  destroyed () {
    this.$store.dispatch('TransactionsStore/clearCurrentTransaction')
    this.$store.dispatch('TransactionsStore/resetExpenseReceipts')
  },
  watch: {
    transactionId: async function (newV, oldV) {
      this.initialized = false
      await this.fetchTransaction()
      await this.fetchPaymentMethods()
      this._initializeExpense()
      this.calculateOptions()
      await this.getBudgetTagTypes(INCLUDE_IN_TRANSACTION_TYPES.EXPENSE)
      this.initialized = true
    }
  }
}
</script>

<style scoped lang="scss" src="@/assets/styles/transactions/transactions-edit.scss"></style>

<style scoped lang="scss">
.Fuell-aeat-arrow-right--pointer {
  cursor: default!important;
}
.multiselect-filter >>> .multiselect .multiselect__tags .multiselect--error {
  border: 2px solid var(--semantic-negative-main);
  border-radius: 6px;
}

.multiselect-filter >>> .multiselect .multiselect__single {
  max-width: 100%;
}

.Fuell-Text {
  font-size: 0.9em;
  margin: 0px 5px;
}

.Fuell-Text--bolder {
  font-weight: 500;
}

.buffer {
  margin: 25px 0px;
}

.full-width {
  max-width: 100% !important;
}

.Form--field-error {
  border: 2px solid var(--semantic-negative-main);

  input,
  .currency-symbol {
    color: var(--semantic-negative-main);
  }
}

.taxes-error {
  color: var(--semantic-negative-main);

  img {
    margin-left: 8px;
  }

  span {
    margin-left: 6.5px;
    font-weight: 400;
    font-size: 12px;
    line-height: 16px;
  }
}

.exchange-rate-block {
  display: flex;
  justify-content: space-between;
  flex-direction: row;

  .transaction-exchange-rate,
  .transaction-default-currency-amount {
    width: 178px;

    .Form-field {
      max-height: 46px;

      input {
        height: 24.5px;
      }
    }
  }
}
</style>
<style lang="scss">
.amount-block {
  display: flex;

  .amount-selector {
    width: 292px;

    .Form-field {
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;

      .icon-multioptions {
        right: 12px;
      }
    }
  }

  .separator-selector {
    width: 1px;
    height: 46px;
    background-color: var(--fill-text-area);

    .line {
      height: 34px;
      margin-top: 6px;
      border-left: 1px solid var(--text-disabled);
    }
  }

  #currency-selector {
    width: 71px;

    .multiselect__tags {
      padding: 0px 20px 0 12px;
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
    }

    .multiselect__content-wrapper {
      width: 364px;
      right: 0;
    }

    .multiselect__single {
      font-size: 13px;
    }
  }
}
</style>
